import {
  Divider,
  Stack,
  type StackProps,
  styled,
  Typography,
  type TypographyProps,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

// biome-ignore lint/style/useNamingConvention: Union types cannot be interfaces
export type TitleColor = "primary" | "secondary";

export interface TitleProps extends StackProps {
  title: string;
  subtitle?: string;
  description?: string;
  /**
   * Color for the title. Affects the color of the title, the divider and the icon.
   * @default "primary"
   */
  color?: TitleColor;
  icon?: React.ReactNode;
  /**
   * Variant of the Typography used for the title.
   * @default "h5"
   */
  titleVariant?: TypographyProps["variant"];
  /**
   * Variant of the Typography used for the subtitle.
   * @default "body1"
   */
  subtitleVariant?: TypographyProps["variant"];
  sx?: SxProps<Theme>;
}

const StyledDivider = styled(Divider, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: TitleColor }>(({ color, theme }) => ({
  width: "1px",
  height: "18px",
  border: 1,
  backgroundColor: theme.palette[color].dark,
}));

export function Title({
  title,
  subtitle,
  description,
  color = "primary",
  icon,
  titleVariant = "h5",
  subtitleVariant = description ? "body1" : "overline",
  ...props
}: TitleProps) {
  return (
    <Stack display="flex" gap={1} alignItems="flex-start" {...props}>
      <Stack
        direction={"row"}
        alignItems="center"
        gap={0.75}
        sx={(theme) => ({
          color:
            color === "primary"
              ? theme.palette.primary.light
              : theme.palette.secondary.light,
        })}
      >
        {icon || null}
        <Typography
          variant={titleVariant}
          sx={(theme) => ({
            color:
              color === "primary"
                ? theme.palette.primary.dark
                : theme.palette.secondary.dark,
          })}
        >
          {title}
        </Typography>
        {subtitle && <StyledDivider color={color} />}
        <Typography
          variant={subtitleVariant}
          sx={(theme) => ({
            color: theme.palette.secondary.dark,
            fontWeight: description
              ? theme.typography.fontWeightSemiBold
              : theme.typography.fontWeightMedium,
          })}
        >
          {subtitle}
        </Typography>
      </Stack>
      {description && <Typography variant="overline">{description}</Typography>}
    </Stack>
  );
}
